import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Search, PlusCircle, Users, Loader2, ChevronRight, Home, Trophy } from "lucide-react";

const BarnRow = ({ b, onOpen }) => (
  <button data-testid={`barn-row-${b.id}`} onClick={() => onOpen(b.id)}
    className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left active:scale-[0.99]">
    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl" style={{ background: b.color_primary || "#3a5a40" }}>
      {b.cover_image && <img src={b.cover_image} alt="" className="h-full w-full object-cover" />}
    </div>
    <div className="min-w-0 flex-1">
      <p className="truncate font-bold text-primary">{b.name} <span className="text-xs font-black" style={{ color: b.color_primary }}>[{b.tag}]</span></p>
      <p className="truncate text-xs text-muted-foreground">
        {b.member_count}/{b.max_members} members · {b.membership_mode}{b.accepting ? "" : " · closed"} · {b.lifetime_points} pts
      </p>
      {b.friend_members?.length > 0 && (
        <p className="mt-0.5 truncate text-[11px] font-semibold text-secondary">{b.friend_members.length} of your friends {b.friend_members.length === 1 ? "is a member" : "are members"}</p>
      )}
    </div>
    <ChevronRight size={16} className="shrink-0 text-muted-foreground" />
  </button>
);

const CreateBarn = ({ onCreated }) => {
  const [name, setName] = useState("");
  const [tag, setTag] = useState("");
  const [desc, setDesc] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = () => {
    setBusy(true);
    gameApi.post("/game/barns/create", { name: name.trim(), tag: tag.trim(), description: desc.trim() })
      .then((r) => { toast.success("Training Barn founded!"); onCreated(r.data.barn_id); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not create barn."))
      .finally(() => { setBusy(false); setConfirm(false); });
  };
  const tagRaw = tag.trim();
  const tagOk = Array.from(tagRaw).length >= 2 && Array.from(tagRaw).length <= 5 && !/\s|[[\]]/.test(tagRaw);
  const valid = name.trim().length >= 3 && tagOk;
  return (
    <div className="space-y-3" data-testid="create-barn-form">
      <label className="block text-sm font-bold text-primary">Barn Name
        <input data-testid="barn-name-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={40}
          placeholder="e.g. Sunflower Horsemanship" className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 outline-none focus:border-primary" />
      </label>
      <label className="block text-sm font-bold text-primary">Barn Tag (2–5 letters/numbers)
        <input data-testid="barn-tag-input" value={tag} onChange={(e) => setTag(e.target.value.toUpperCase())} maxLength={20}
          placeholder="SFH or ℭ𝔇𝔈" className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 font-black uppercase outline-none focus:border-primary" />
      </label>
      <label className="block text-sm font-bold text-primary">Description
        <textarea data-testid="barn-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} maxLength={1000} rows={3}
          placeholder="Tell players what your barn is about…" className="mt-1 w-full rounded-xl border border-border bg-background p-3 text-sm outline-none focus:border-primary" />
      </label>
      <button data-testid="barn-create-btn" disabled={!valid || busy} onClick={() => setConfirm(true)}
        className="h-12 w-full rounded-xl bg-primary font-bold text-primary-foreground disabled:opacity-40 active:scale-95">
        Found Barn — 50,000 coins
      </button>
      {!valid && (
        <p className="text-xs font-semibold text-amber-600" data-testid="barn-create-hint">
          {name.trim().length < 3 ? "Barn name needs at least 3 characters. " : ""}
          {!tagOk ? "Tag must be 2–5 characters (any letters, numbers or symbols) with no spaces or brackets." : ""}
        </p>
      )}
      {confirm && (
        <div className="fixed inset-0 z-[120] grid place-items-center bg-black/60 p-4" data-testid="barn-create-confirm" onClick={() => setConfirm(false)}>
          <div className="w-full max-w-sm rounded-2xl bg-card p-6" onClick={(e) => e.stopPropagation()}>
            <p className="font-display text-lg font-bold text-primary">Found “{name.trim()}” [{tag.trim().toUpperCase()}]?</p>
            <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
              <li>• Costs <b className="text-primary">50,000 coins</b>, taken from your balance.</li>
              <li>• The fee is <b>non-refundable</b>.</li>
              <li>• You can only belong to one Training Barn at a time.</li>
            </ul>
            <div className="mt-4 flex gap-2">
              <button data-testid="barn-create-confirm-btn" disabled={busy} onClick={submit} className="h-11 flex-1 rounded-xl bg-primary font-bold text-primary-foreground active:scale-95">
                {busy ? <Loader2 className="mx-auto animate-spin" size={18} /> : "Confirm & Pay"}
              </button>
              <button onClick={() => setConfirm(false)} className="h-11 flex-1 rounded-xl border border-border font-bold text-muted-foreground active:scale-95">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const BarnsHub = ({ onBack }) => {
  const nav = useNavigate();
  const [mine, setMine] = useState(undefined);
  const [board, setBoard] = useState(false);
  const [tab, setTab] = useState("find");
  const [q, setQ] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  useEffect(() => { gameApi.get("/game/barns/mine").then((r) => setMine(r.data)).catch(() => setMine({ barn: null })); }, []);
  const openBarn = (id) => nav(`/game/barn/${id}`);
  const search = () => {
    gameApi.get(`/game/barns/find/search?q=${encodeURIComponent(q.trim())}&accepting=${accepting}`)
      .then((r) => { setResults(r.data.results); setSearched(true); }).catch(() => setResults([]));
  };
  useEffect(() => { if (tab === "find") search(); /* eslint-disable-next-line */ }, [accepting]);

  if (mine === undefined) return <div className="p-10 text-center text-muted-foreground" data-testid="barns-loading"><Loader2 className="mx-auto animate-spin" /></div>;

  return (
    <div className="pb-10" data-testid="barns-hub">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-card/95 px-4 py-3 backdrop-blur">
        <button data-testid="barns-back" onClick={onBack} className="rounded-full p-1 text-muted-foreground"><ArrowLeft size={20} /></button>
        <h2 className="font-display text-xl font-bold text-primary">Training Barns</h2>
        <button data-testid="barns-leaderboard-toggle" onClick={() => setBoard((v) => !v)} className="ml-auto flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-bold text-primary active:scale-95"><Trophy size={13} /> {board ? "Back" : "Rankings"}</button>
      </div>
      {board && <BarnLeaderboard onOpen={openBarn} />}
      <div className="px-4 pt-3" hidden={board}>
        {mine.barn ? (
          <div className="rounded-2xl border border-border bg-card p-4" data-testid="my-barn-card">
            <p className="text-xs font-bold uppercase tracking-wider text-secondary">Your Barn</p>
            <button onClick={() => openBarn(mine.barn.id)} className="mt-2 flex w-full items-center gap-3 text-left">
              <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl" style={{ background: mine.barn.color_primary || "#3a5a40" }}>
                {mine.barn.cover_image && <img src={mine.barn.cover_image} alt="" className="h-full w-full object-cover" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-lg font-bold text-primary">{mine.barn.name} <span className="font-black" style={{ color: mine.barn.color_primary }}>[{mine.barn.tag}]</span></p>
                <p className="text-xs text-muted-foreground"><Users size={11} className="mr-1 inline" />{mine.barn.member_count}/{mine.barn.max_members} · You are {mine.role}</p>
              </div>
              <ChevronRight size={18} className="text-muted-foreground" />
            </button>
            <button data-testid="open-my-barn-btn" onClick={() => openBarn(mine.barn.id)}
              className="mt-3 h-11 w-full rounded-xl bg-primary font-bold text-primary-foreground active:scale-95">Open Barn Home</button>
          </div>
        ) : (
          <>
            <div className="mb-3 flex gap-1 rounded-xl bg-muted p-1">
              <button data-testid="barns-tab-find" onClick={() => { setTab("find"); if (!searched) search(); }} className={`flex-1 rounded-lg py-1.5 text-xs font-bold ${tab === "find" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Find a Barn</button>
              <button data-testid="barns-tab-create" onClick={() => setTab("create")} className={`flex-1 rounded-lg py-1.5 text-xs font-bold ${tab === "create" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Create a Barn</button>
            </div>
            {tab === "find" ? (
              <>
                <div className="mb-2 flex items-center gap-2 rounded-xl border border-border bg-card p-1.5">
                  <Search size={16} className="ml-1 text-muted-foreground" />
                  <input data-testid="barn-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Search barns by name or tag…" className="min-w-0 flex-1 bg-transparent text-sm outline-none" />
                  <button data-testid="barn-search-btn" onClick={search} className="rounded-lg bg-secondary px-3 py-1.5 text-xs font-bold text-white active:scale-95">Search</button>
                </div>
                <label className="mb-3 flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                  <input data-testid="barn-accepting-filter" type="checkbox" checked={accepting} onChange={(e) => setAccepting(e.target.checked)} /> Only barns accepting members
                </label>
                <div className="space-y-2" data-testid="barn-search-results">
                  {results.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">{searched ? "No barns found." : "Search to discover Training Barns."}</p>
                    : results.map((b) => <BarnRow key={b.id} b={b} onOpen={openBarn} />)}
                </div>
              </>
            ) : (
              <CreateBarn onCreated={(id) => openBarn(id)} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const BarnLeaderboard = ({ onOpen }) => {
  const [period, setPeriod] = useState("overall");
  const [data, setData] = useState(null);
  useEffect(() => { gameApi.get(`/game/barns/leaderboard/board?period=${period}`).then((r) => setData(r.data)).catch(() => setData({ rows: [], my_barn: null })); }, [period]);
  return (
    <div className="px-4 pt-3" data-testid="barn-leaderboard">
      <div className="mb-3 flex gap-1 rounded-xl bg-muted p-1">
        <button data-testid="lb-tab-overall" onClick={() => setPeriod("overall")} className={`flex-1 rounded-lg py-1.5 text-xs font-bold ${period === "overall" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Overall</button>
        <button data-testid="lb-tab-weekly" onClick={() => setPeriod("weekly")} className={`flex-1 rounded-lg py-1.5 text-xs font-bold ${period === "weekly" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Weekly</button>
      </div>
      {data?.my_barn && (
        <button data-testid="lb-my-barn" onClick={() => onOpen(data.my_barn.id)} className="mb-2 flex w-full items-center gap-2 rounded-xl border border-primary/40 bg-primary/5 p-2.5 text-left">
          <span className="text-sm font-bold text-primary">Your Barn: #{data.my_barn.rank}</span>
          <span className="flex-1 truncate text-sm text-muted-foreground">{data.my_barn.name} [{data.my_barn.tag}]</span>
          <span className="text-sm font-extrabold text-secondary">{data.my_barn.points.toLocaleString()}</span>
        </button>
      )}
      {!data ? <Loader2 className="mx-auto animate-spin" /> : (
        <div className="space-y-1.5" data-testid="lb-rows">
          {data.rows.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">No barns ranked yet.</p> : data.rows.map((r) => (
            <button key={r.id} data-testid={`lb-barn-row-${r.id}`} onClick={() => onOpen(r.id)} className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-2.5 text-left active:scale-[0.99]">
              <span className="w-7 text-center text-sm font-extrabold text-muted-foreground">#{r.rank}</span>
              <span className="h-7 w-7 shrink-0 rounded-lg" style={{ background: r.color_primary || "#3a5a40" }} />
              <span className="min-w-0 flex-1 truncate font-bold text-primary">{r.name} <span className="text-xs font-black text-secondary">[{r.tag}]</span> <span className="text-[11px] font-normal text-muted-foreground">· {r.member_count}👥</span></span>
              <span className="text-sm font-extrabold text-secondary">{r.points.toLocaleString()}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
